"use client";

import type { CalendarViewMode } from "@mlx/contracts";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { SegmentedControl } from "@/components/shared/segmented-control";
import { Button } from "@/components/ui/button";

/*
 * The calendar's controls row at rest, as markup. RecordCalendarControls
 * works out the heading and whether the period is today's, and moves the
 * calendar; a fixture passes the heading and scale it shows.
 */

const MODES: Array<{ value: CalendarViewMode; label: string }> = [
  { value: "month", label: "Month" },
  { value: "week", label: "Week" },
  { value: "day", label: "Day" },
  { value: "agenda", label: "Agenda" },
];

const keepScale = () => {};

/** Today, previous and next, the period's heading, the calendar's timezone, then its scale. */
export function RecordCalendarControlsView({
  heading,
  timezone,
  mode,
  atToday = false,
  onToday,
  onPrevious,
  onNext,
  onModeChange,
}: {
  /** The period shown, such as "September 2026". */
  heading: string;
  /** The calendar's IANA timezone, such as "Europe/London". */
  timezone: string;
  mode: CalendarViewMode;
  /** The period shown holds today, so Today is disabled. */
  atToday?: boolean;
  onToday?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onModeChange?: (mode: CalendarViewMode) => void;
}) {
  return (
    // On a phone the row is wider than the screen: the scale then takes a
    // line of its own rather than the controls overlapping.
    <div className="flex min-h-12 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border/70 bg-background px-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 active:scale-[0.97]"
          disabled={atToday}
          onClick={onToday}
        >
          Today
        </Button>
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Previous period"
            onClick={onPrevious}
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Next period"
            onClick={onNext}
          >
            <ChevronRightIcon />
          </Button>
        </div>
        <h2 className="truncate text-sm font-semibold tracking-tight">
          {heading}
        </h2>
      </div>
      <span className="ml-auto text-xs text-muted-foreground" title="Calendar timezone">{timezone}</span>
      <SegmentedControl
        aria-label="Calendar scale"
        value={mode}
        onValueChange={onModeChange ?? keepScale}
        options={MODES}
      />
    </div>
  );
}
